import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { request } from "../lib/api";
import { translatableStrings } from "../lib/translatable";
import type { ContentItemDetail, Notice } from "../types";
import { Upload } from "../components/Upload";
import {
  Card,
  InlineNotice,
  errorMessage,
  inputClass,
  labelClass,
  primaryButtonClass,
  secondaryButtonClass,
  textareaClass,
} from "../components/ui";

const LANGUAGE = "ur";

// Direct link to one item's Urdu translation (/content/:id/urdu).
export function ContentTranslation() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  if (!id) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-interactive hover:underline"
      >
        <ArrowLeft size={16} /> Back to content
      </button>
      <ContentTranslationEditor id={id} />
    </>
  );
}

// Urdu wording for one content item. Each English string in the item gets an
// Urdu counterpart; teachers using the app in Urdu see it, and anything left
// empty stays in English. Answers are graded by ID, so translating never
// changes which option is correct.
export function ContentTranslationEditor({ id, onClose }: { id: string; onClose?: () => void }) {
  const [item, setItem] = useState<ContentItemDetail | null>(null);
  const [title, setTitle] = useState("");
  const [strings, setStrings] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<Notice | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    request<ContentItemDetail>(`/api/admin/content-items/${id}`)
      .then((data) => {
        const saved = data.translations?.[LANGUAGE];
        setItem(data);
        setTitle(saved?.title ?? "");
        // Older saves stored keys trimmed; map them back to the exact text.
        const stored = saved?.strings ?? {};
        setStrings(
          Object.fromEntries(
            translatableStrings(data.content).map((entry) => [
              entry.source,
              stored[entry.source] ?? stored[entry.source.trim()] ?? "",
            ])
          )
        );
      })
      .catch((cause) => setMessage({ tone: "error", text: errorMessage(cause) }));
  }, [id]);

  const entries = useMemo(() => (item ? translatableStrings(item.content) : []), [item]);
  const done = entries.filter((entry) => strings[entry.source]?.trim()).length;
  const update = (source: string, value: string) =>
    setStrings((current) => ({ ...current, [source]: value }));

  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    // Only strings the item still contains are kept, so edits to the English
    // content don't leave stale translations behind.
    const current = Object.fromEntries(
      entries
        .filter((entry) => strings[entry.source]?.trim())
        .map((entry) => [entry.source, strings[entry.source]])
    );
    try {
      await request(`/api/admin/content-items/${id}/translations/${LANGUAGE}`, {
        method: "PUT",
        body: JSON.stringify({ title, strings: current }),
      });
      setMessage({ tone: "success", text: "Urdu translation saved." });
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause) });
    } finally {
      setBusy(false);
    }
  }

  if (!item) {
    return (
      <Card title="Urdu translation">
        {message ? (
          <InlineNotice tone={message.tone} text={message.text} />
        ) : (
          <p className="flex items-center gap-2 text-sm text-content-secondary">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </p>
        )}
      </Card>
    );
  }

  return (
    <form onSubmit={save}>
      <Card
        title={`Urdu translation · ${item.title}`}
        text={`Teachers using the app in Urdu see these. Anything left empty is shown in English. ${done} of ${entries.length} translated.`}
      >
        <div className="grid gap-5">
          <Row label="Title" english={item.title}>
            <input
              className={inputClass}
              dir="rtl"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Row>
          {entries.map((entry) => (
            <Row
              key={entry.source}
              label={entry.label}
              english={entry.source}
              isFile={entry.file !== undefined}
            >
              {entry.file === undefined ? (
                <textarea
                  className={textareaClass}
                  dir="rtl"
                  rows={Math.min(8, Math.max(2, Math.ceil(entry.source.length / 70)))}
                  value={strings[entry.source] ?? ""}
                  onChange={(e) => update(entry.source, e.target.value)}
                />
              ) : (
                <div className="grid gap-2">
                  {entry.file && (
                    <Upload
                      purpose={entry.file}
                      label="Urdu file"
                      value={strings[entry.source] || null}
                      onUploaded={(url) => update(entry.source, url)}
                      onRemove={() => update(entry.source, "")}
                    />
                  )}
                  <input
                    className={inputClass}
                    value={strings[entry.source] ?? ""}
                    onChange={(e) => update(entry.source, e.target.value)}
                    placeholder="Or paste the Urdu file link (optional)"
                  />
                </div>
              )}
            </Row>
          ))}
        </div>
        {message && <InlineNotice tone={message.tone} text={message.text} />}
        <div className="mt-6 flex flex-wrap gap-3">
          <button className={primaryButtonClass} disabled={busy}>
            {busy && <Loader2 size={16} className="animate-spin" />}
            Save Urdu translation
          </button>
          {onClose && (
            <button type="button" className={secondaryButtonClass} onClick={onClose}>
              Close
            </button>
          )}
        </div>
      </Card>
    </form>
  );
}

function Row({
  label,
  english,
  isFile = false,
  children,
}: {
  label: string;
  english: string;
  isFile?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="grid gap-3 border-b border-border pb-5 last:border-0 md:grid-cols-2">
      <div className="min-w-0">
        <span className={labelClass}>{label}</span>
        <p
          className={`mt-1 text-sm text-content-secondary ${isFile ? "break-all" : "whitespace-pre-wrap"}`}
        >
          {english}
        </p>
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}
